import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useT } from "@/lib/i18n";
import { isGuest } from "@/lib/guest";

/**
 * Persistent banner shown to guests on quota-gated pages.
 * If `remaining` is provided, displays the count and CTA to register.
 */
export default function GuestBanner({ feature, remaining }) {
  const { lang } = useT();
  if (!isGuest()) return null;

  const isExhausted = remaining === 0;

  return (
    <div
      data-testid={`guest-banner-${feature}`}
      className={`rounded-2xl px-4 py-3 mb-5 flex items-start gap-3 ${
        isExhausted
          ? "bg-[#FCE4ED] border border-[#E5276A]/30"
          : "bg-[#FBF7F3] border border-[#1F3A5F]/12"
      }`}
    >
      <Sparkles className="w-4 h-4 text-[#E5276A] shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        {isExhausted ? (
          <p className="text-[#1F3A5F]">
            <b>{lang === "fr" ? "Quota gratuit atteint pour aujourd'hui." : "Free quota reached for today."}</b>{" "}
            {lang === "fr"
              ? "Créez un compte gratuit pour un usage illimité — moins d'une minute."
              : "Create a free account for unlimited use — less than a minute."}
          </p>
        ) : (
          <p className="text-[#4A5568]">
            <b className="text-[#1F3A5F]">
              {lang === "fr" ? "Mode invité" : "Guest mode"}
            </b>
            {typeof remaining === "number" && (
              <> · <span className="text-[#E5276A] font-bold">
                {lang === "fr"
                  ? `${remaining} essai${remaining > 1 ? "s" : ""} gratuit${remaining > 1 ? "s" : ""} restant${remaining > 1 ? "s" : ""} aujourd'hui`
                  : `${remaining} free attempt${remaining > 1 ? "s" : ""} left today`}
              </span></>
            )}
            <span className="block mt-0.5 text-[#8B95A1] text-xs">
              {lang === "fr" ? "Inscrivez-vous gratuitement pour usage illimité + sauvegarde de vos contenus." : "Sign up free for unlimited use + content saving."}
            </span>
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Link
          to="/register"
          data-testid={`guest-banner-${feature}-register`}
          className="shrink-0 px-3 py-1.5 rounded-xl bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]"
        >
          {lang === "fr" ? "Créer mon compte" : "Sign up"}
        </Link>
      </div>
    </div>
  );
}
